import { observeCameraRemoval, traceCameraRemove } from '#~/device/remove-trace.js';
import {
  openClaimBootstrapObservation,
  recordClaimBootstrapPhase,
  type ClaimBootstrapObservationRun
} from '#~/test-probes/claim-bootstrap-observation.js';
/* eslint-disable @typescript-eslint/no-use-before-define */
import {
  agentSnapshotRevision,
  clearAgentSnapshot,
  forgetAgentSnapshotRead,
  loadAgentSnapshot
} from './snapshots.js';
import { isDeviceDeleted } from '#~/device/commands/teardown.js';
import { runner, unwrap } from '@studio/js-utils/async';
import type { Agent } from '@studio/models/agent';
import type { AppId } from '@studio/models/app';
import {
  isPendingDevice,
  isTransient,
  setPendingDevice,
  unsetPendingDevice
} from '@studio/models/device';
import type { Model } from '@studio/models/device';
import type { Token } from '@studio/models/livekit';
import { toAuthentication } from '@studio/models/livekit';
import type { Room } from '@studio/models/room';
import { messageTypeRegistry } from '@tryiris-ai/common.protojs';
import { State } from '@tryiris-ai/common.protojs/agent/v1/agent';
import type { StateValue } from '@tryiris-ai/common.protojs/state/v1/state';
import { schema } from '@tryiris-ai/common.protojs/schema';
import type { T_Maybe, T_Optional } from '@tryiris-ai/js-types';
import { SyncLogger, SyncOptions, SyncSubordinate } from '@tryiris-ai/studio.state-sync-ts';
import { defaults } from 'lodash-es';
import type { Opaque } from 'type-fest';
import { AutoTrackingManager } from '../auto-tracking/index.js';
import { ProxyImpl, setRetainedRowResolver } from './proxy.js';
import type { Handlers, Options, Proxy, Snapshot, Transaction } from './types.js';
import { toHexFromId } from '#~/api/index.js';
import { retryWhenReachable } from '#~/boot/retry-when-reachable.js';
import { createToken } from '#~/api/livekit/index.js';
import { depot } from '#~/depot/index.js';
import { toDevice } from '#~/device/index.js';
import type { Device, Minimist } from '#~/device/index.js';
// Accepted live sources must release stale deletion tombstones.
import { clearDeviceDeleted } from '#~/device/commands/teardown.js';
import { getBrands, getTemplate } from '#~/device/registry/commands/index.js';
import { StudioError } from '#~/error/index.js';
import { logger } from '#~/logger/index.js';
import { clear as clearNotification, raiseError } from '#~/notifications/index.js';
import type { Notification } from '#~/notifications/index.js';
// W12 (Track 4a) — observability for last-known-good cache load on
// page boot. Per logger/observability.ts the call site here is the
// canonical aggregation point (loadSnapshot), already deduped via the
// generation-owned snapshot promise, so each successful load logs once.
import { logCacheLoad, logStateSyncEvent } from '#~/logger/observability.js';
import { makeRoomBroker } from '#~/models/broker/index.js';
import { nats } from '#~/nats/index.js';
import { jsToPbControl, pbToJsDevice } from '#~/protobuf/index.js';
import { startAgent, retireAgentStart } from './provider/start-signal.js';
import { restoreIPControllers } from '#~/ipjoystick/index.js';
import { resetPipelines } from '#~/mediadevices/pipeline.js';
import type { RTMPDestinationsMutationRecords } from '#~/room/components/production/rtmp/store/destinations.js';
import { StudioDevice } from '@tryiris-ai/common.protojs/studio/v1/studio';
import { cleanupDeviceByHex } from './device-cleanup.js';
import { track } from '#~/analytics/track.js';
import { EVENT_CATEGORIES, EVENT_NAMES, EVENT_SEVERITY } from '#~/analytics/constants/index.js';
import type { AnalyticsEvent } from '#~/analytics/types/index.js';
import { resolveDeviceAnalyticsContextFromDevice } from '#~/analytics/device-context.js';
import type { DeviceAnalyticsCatalogModelLike } from '#~/analytics/device-context.js';
import { resolveLinkingSurface } from '#~/analytics/domains/linking-surface.js';
import {
  commitShadowKind,
  isPriorProcessShadow,
  readShadowKind,
  rememberPriorProcessShadow
} from '#~/analytics/domains/shadow-kind.js';
import { disarmDeviceShadowTimeout } from '#~/analytics/domains/shadow-timeout.js';
import { toEmbeddedShadowDeviceId } from '#~/device/embedded-shadow-id.js';
import { isElectronTrayWindow } from '#~/routes/electron/tray/utils.js';
import { isElectronApp } from '#~/electron/utils.js';
import {
  dropPersistedTransitoryDevices,
  syncPersistedTransitoryDevices
} from '#~/device/transitory.js';
import { pendingAuthPlaceholders, pendingAuthReplayRecords } from '#~/device/transitory-record.js';
//import { NewtsConnection } from '#~/newts/connection.js';
//import { connections } from '#~/newts/connections.js';

// ENG-3885: UI recovery affordance for permanent restore failures.
//
// Before the fix, the three restore functions threw `StudioError` from a
// fire-and-forget caller (`void restoreDevice(...)`), so a permanent
// failure produced an unhandled promise rejection AND silently dropped
// the user's device from the studio UI. Now the rejection is caught at
// the call site, the failure is surfaced via the existing `raiseError`
// notification primitive (with `autoClose: false` so it persists), and
// the failed restore is tracked in `failedRestores` so the user can
// invoke `retryFailedRestore(key)` from the matching UI surface (see
// `components/agent-restore-failure-notice.tsx`).
//
// Per `think-in-systems`: a single error funnel for all three restore
// kinds avoids three bespoke handlers and keeps the retry primitive
// uniform.
export type RestoreKind = 'device' | 'recordingDirectory' | 'rtmpDestinations' | 'snapshot';

const RESTORE_KIND_TITLE: Record<RestoreKind, string> = {
  snapshot: 'Sources not restored',
  device: 'Device not restored',
  recordingDirectory: 'Recording directory not restored',
  rtmpDestinations: 'Streaming destinations not restored'
};

const restoreFailureSubscribers = new Set<() => void>();

/**
 * Map of failed restores, keyed by `${agentId}:${kind}:${id}`. The agentId
 * prefix lets `broker.destroy` purge entries owned by a destroyed agent
 * (preventing stale closures over stopped proxies) and avoids cross-agent key
 * collisions (e.g. `rtmpDestinations:snapshot`).
 */
interface FailedRestore {
  readonly retry: () => Promise<void>;
  readonly notificationId: Notification['id'];
  // Readable resource name shared by the failure list and notification.
  readonly name: string;
  // Stops the reachability poll that retries this entry by itself.
  readonly stopAutoRetry: () => void;
}

export const failedRestores = new Map<string, FailedRestore>();

/** Auto-retries per failed restore once the API answers; then manual Retry only. */
export const MAX_AUTO_RESTORE_RETRIES = 3;

/**
 * Tracks `retryFailedRestore` invocations currently in-flight per key so
 * rapid double-clicks on the UI Retry button don't run two restore closures
 * concurrently for the same entry.
 */
const inFlightRetries = new Set<string>();

/**
 * How long an embedded shadow add episode waits, after its retry loop stops,
 * for an attempt still in flight before it reports the outcome it has. Long
 * enough for a slow but successful insert to report device_add; bounded so a
 * request that never answers still ends in device_add_failed, marked
 * `add_unresolved` because that request may still land.
 */
export const SHADOW_ADD_SETTLE_GRACE_MS = 30_000;

/**
 * How often a settled-but-undecided add episode looks again while only a
 * pending placeholder (10s TTL, no add lock) stands in its way.
 */
export const SHADOW_ADD_PENDING_RECHECK_MS = 2_500;

/**
 * Operation-lifetime add lock keyed by device identity.
 * `isPendingDevice` is a 10s TTL and is not enough: a hung createToken/mutate
 * must still block a second add until this entry is released.
 * See knowledge: addDevice-single-flight-pattern (ai.knowledge/studio.client).
 * Per rule: settle-every-terminal-branch (ai.knowledge/global) — release in finally.
 */
export const inFlightDeviceAdds = new Set<Device['id']>();

// Initial and reset bootstraps can overlap on one connection. Reporting belongs
// to its current reset interval; independent Proxy objects never share a claim.
// Per rule: regression-tests-for-state-machine-primitives (ai.knowledge/global).
const shadowReportState = new WeakMap<Proxy, 'reported' | 'stopped'>();

// A reset can empty control.device after an acknowledged insertion. Its native
// restoration must continue, but that does not make this camera a new add.
// Keep only confirmed reporting facts across connection replacements, scoped
// by the existing Studio/device key. A shadow alone cannot establish an add.
// See knowledge: addDevice-single-flight-pattern (ai.knowledge/studio.client).
const confirmedShadowAdds = new Set<string>();

/** @internal Tests reset renderer-lifetime reporting without changing real reset behavior. */
export const resetShadowAddReportsForTests = (): void => {
  confirmedShadowAdds.clear();
};

/** True in the tray renderer from its first line of code (see the shadow gate in restoreAgent). */
const isTrayRenderer = (): boolean => isElectronApp('tray') || isElectronTrayWindow();

const notifyRestoreFailureSubscribers = (): void => {
  restoreFailureSubscribers.forEach((cb) => {
    try {
      cb();
    } catch (e) {
      logger.error(e as Error);
    }
  });
};

const clearRestoreFailureNotificationIfUnused = (notificationId: Notification['id']): void => {
  // ENG-3885: deduped notification IDs stay visible until every matching restore recovers.
  if (![...failedRestores.values()].some((entry) => entry.notificationId === notificationId)) {
    clearNotification(notificationId);
  }
};

const toRestoreFailureMapKey = (kind: RestoreKind, key: string, agentId?: Agent['id']): string =>
  agentId ? `${agentId}:${kind}:${key}` : `${kind}:${key}`;

const clearRestoreFailure = (mapKey: string, expected?: FailedRestore): boolean => {
  const failedRestore = failedRestores.get(mapKey);
  if (!failedRestore || (expected && failedRestore !== expected)) {
    return false;
  }

  failedRestores.delete(mapKey);
  failedRestore.stopAutoRetry();
  clearRestoreFailureNotificationIfUnused(failedRestore.notificationId);
  notifyRestoreFailureSubscribers();
  return true;
};

const primeDeviceAnalyticsRegistry = async (
  modelId: T_Maybe<Model['id']>,
  token: string
): Promise<T_Maybe<DeviceAnalyticsCatalogModelLike>> => {
  if (!modelId) {
    return undefined;
  }

  // Per ENG-3959 — restore/shadow analytics fire during boot before registry hooks hydrate.
  const [templateResult, brandsResult] = await Promise.allSettled([
    getTemplate(modelId, token),
    getBrands(token)
  ]);

  if (templateResult.status === 'rejected') {
    logger.warning(`Unable to prime analytics registry metadata for model ${modelId}`);
    return undefined;
  }

  // Per ENG-4456 — hand the fetched model straight back to the caller instead of only
  // writing it to the shared registry store. The cold-boot device_restore emit that
  // triggered this fetch used to re-read that store afterward and take whatever shape
  // resolveDeviceAnalyticsCatalogContext's registry-only lookup happened to see; passing
  // the resolved model through directly removes that indirection entirely.
  const { model } = templateResult.value;

  if (!model) {
    return undefined;
  }

  const brandName =
    brandsResult.status === 'fulfilled'
      ? brandsResult.value.items?.[model.brandId]?.name
      : undefined;

  return {
    id: model.id,
    name: model.name,
    brandId: model.brandId,
    ...(brandName ? { brandName } : {})
  };
};

/**
 * Subscribe to changes in the failed-restores map. Returns an unsubscribe
 * function. The UI surface (`AgentRestoreFailureNotice`) uses this hook to
 * re-render when a restore fails or is retried.
 */
export const subscribeToFailedRestores = (cb: () => void): (() => void) => {
  restoreFailureSubscribers.add(cb);
  return () => {
    restoreFailureSubscribers.delete(cb);
  };
};

/**
 * Manually retry a failed restore. Removes the entry from `failedRestores`
 * on success; leaves it in place on failure so the user can retry again.
 */
export const retryFailedRestore = async (key: string): Promise<void> => {
  const failedRestore = failedRestores.get(key);
  if (!failedRestore) {
    return;
  }
  // ENG-3885: guard against concurrent retries for the same key (e.g.
  // rapid double-clicks on the UI Retry button). Without this, two
  // `restoreDevice` chains could run in parallel, each performing up to
  // 6 mutate attempts.
  if (inFlightRetries.has(key)) {
    return;
  }
  inFlightRetries.add(key);
  try {
    await failedRestore.retry();
    clearRestoreFailure(key, failedRestore);
  } catch (e) {
    logger.error(e as Error);
    // Leave the entry in place so the user can try again.
  } finally {
    inFlightRetries.delete(key);
  }
};

/** @internal */
export const handleRestoreFailure = (
  kind: RestoreKind,
  key: string,
  error: unknown,
  retry: () => Promise<void>,
  agentId?: Agent['id'],
  name?: string
): void => {
  // ENG-3885: scope the key by agentId when available so multiple agents
  // can each track their own failed restores without colliding (notably
  // for the `rtmpDestinations:snapshot` case where the kind+id alone
  // isn't unique across agents). `broker.destroy` uses the prefix to
  // purge entries belonging to a destroyed agent.
  const mapKey = toRestoreFailureMapKey(kind, key, agentId);
  clearRestoreFailure(mapKey);

  // Fall back to the stable device ID when no readable name is available.
  const displayName = name && name.length > 0 ? name : key;

  // Keep the original error as the diagnostic cause of the user-facing notice.
  const studioError = new StudioError(
    `${displayName} could not be restored. Retry from Restore failures.`,
    { cause: error instanceof Error ? error : undefined }
  );

  // ENG-3885: autoClose=false so the notification persists until the
  // user dismisses it or retries via `AgentRestoreFailureNotice`. The
  // existing `notifications/components/notice` + `panel` surfaces
  // render the toast — no new design-system component needed.
  // actionable:true lights the masthead bell (#1776 squash leftover).
  const notificationId = raiseError(studioError, {
    severity: 'error',
    title: RESTORE_KIND_TITLE[kind],
    autoClose: false,
    closeable: true,
    actionable: true
  });
  // A restore that failed while the WAN was down (its 120 s deadline ran out
  // waiting for a cloud token) retries by itself once the API answers again,
  // through the same path as the Retry button; a failed retry re-arms. Without
  // this, video stayed off after the uplink came back until someone clicked
  // Retry. Per rule: settle-every-terminal-branch
  // (ai.knowledge/global): clearRestoreFailure and agent destroy stop the poll.
  // Bounded: a restore that keeps failing with the API up is not an outage,
  // so after MAX_AUTO_RESTORE_RETRIES it is left to the Retry button.
  let stopAutoRetry = (): void => {};
  let autoRetries = 0;
  const arm = (): void => {
    stopAutoRetry = retryWhenReachable(() => {
      autoRetries += 1;
      void retryFailedRestore(mapKey).then(() => {
        if (failedRestores.get(mapKey) === entry && autoRetries < MAX_AUTO_RESTORE_RETRIES) arm();
      });
    });
  };
  const entry: FailedRestore = {
    retry,
    notificationId,
    name: displayName,
    stopAutoRetry: () => stopAutoRetry()
  };
  failedRestores.set(mapKey, entry);
  arm();
  notifyRestoreFailureSubscribers();
};

/** @internal */
export const restoreDevice = async (
  proxy: Proxy,
  device: Device,
  token: Token,
  endpoint: string,
  retry: number = 0,
  isCurrent: () => boolean = () => true,
  deadline: number = Date.now() + 120_000
): Promise<void> => {
  requireRestoreCurrent(isCurrent, deadline);
  traceCameraRemove('restore.attempt', {
    deviceId: device.id,
    agentId: proxy.id,
    retry,
    blockedByDeletion: isDeviceDeleted(device.id)
  });
  // A restore that outlives deletion must not bring that camera back.
  if (isDeviceDeleted(device.id)) return;
  // Materialize the protobuf device.
  const pbControl = jsToPbControl(device, {
    liveKit: toAuthentication(proxy.roomId, endpoint, token)
  });

  if (pbControl) {
    try {
      if (isPendingDevice(device.id)) {
        return;
      }

      setPendingDevice({
        id: device.id,
        name: device.name,
        embedded: proxy.embedded,
        agentId: proxy.id
      });

      // Optimistic state can render immediately; the awaited transport acknowledgement is still bounded.
      await awaitRestore(
        proxy,
        proxy.mutate({ control: pbControl }, {}, true),
        isCurrent,
        deadline
      );

      logger.debug(`Restored device from persistent storage: ${device.id}`);

      return;
    } catch (e) {
      requireRestoreCurrent(isCurrent, deadline);
      unsetPendingDevice(device.id);

      logger.error(`Failed to restore device from persistent storage: ${device.id}`, e);

      // ENG-3885: drop the `retry &&` truthy-coerce. On the first failure
      // `retry === 0` is falsy, so the original guard skipped retries
      // entirely and threw on attempt #1 — the opposite of what commit
      // 3d3114dfc6 ("add retry routine for device restore") promised.
      if (retry < 5) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        await restoreDevice(proxy, device, token, endpoint, retry + 1, isCurrent, deadline);
      } else {
        throw new StudioError('Unable to restore device from persistent storage', { cause: e });
      }
    }
  }

  if (!pbControl) throw new StudioError(`Unable to materialize saved device: ${device.id}`);
};

/** @internal */
export const restoreRecordingDirectory = async (
  proxy: Proxy,
  recordingPath: string,
  retry: number = 0,
  isCurrent: () => boolean = () => true,
  deadline: number = Date.now() + 120_000
): Promise<void> => {
  requireRestoreCurrent(isCurrent, deadline);
  const mutation = {
    control: {
      studio: {
        recordingDirectory: recordingPath
      }
    }
  };

  try {
    // Preserve optimistic presentation while bounding the acknowledgement and all retries.
    await awaitRestore(proxy, proxy.mutate(mutation, {}, true), isCurrent, deadline);
  } catch (cause) {
    requireRestoreCurrent(isCurrent, deadline);
    logger.error(
      `Failed to restore recording directory from persistent storage: ${recordingPath}`,
      cause
    );

    // ENG-3885: see restoreDevice — same falsy-zero retry-guard regression.
    if (retry < 5) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await restoreRecordingDirectory(proxy, recordingPath, retry + 1, isCurrent, deadline);
    } else {
      throw new StudioError('Unable to restore recording directory in state', { cause });
    }
  }
};

/**
 * Restores the RTMP destinations in the agent's state.
 *
 * @param {Proxy} proxy - The proxy instance used to perform the mutation.
 * @param {RTMPDestinations} rtmpDestinations - The RTMP destinations to restore.
 * @throws {StudioError} If unable to restore RTMP destinations in state.
 */
/** @internal */
export const restoreRTMPDestinations = async (
  proxy: Proxy,
  rtmpDestinations: RTMPDestinationsMutationRecords,
  retry: number = 0,
  isCurrent: () => boolean = () => true,
  deadline: number = Date.now() + 120_000
): Promise<void> => {
  requireRestoreCurrent(isCurrent, deadline);
  const mutation = {
    control: {
      studio: {
        rtmpEgress: {
          rtmpOptions: undefined,
          destination: rtmpDestinations
        }
      }
    }
  };

  try {
    // Preserve optimistic presentation while bounding the acknowledgement and all retries.
    await awaitRestore(proxy, proxy.mutate(mutation, {}, true), isCurrent, deadline);
  } catch (cause) {
    requireRestoreCurrent(isCurrent, deadline);
    // ENG-3885: see restoreDevice — same falsy-zero retry-guard regression.
    if (retry < 5) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      await restoreRTMPDestinations(proxy, rtmpDestinations, retry + 1, isCurrent, deadline);
    } else {
      throw new StudioError('Unable to restore RTMP destinations in state', { cause });
    }
  }
};

const requireRestoreCurrent = (current: () => boolean, deadline: number): void => {
  if (!current()) throw new DOMException('Restore owner retired', 'AbortError');
  if (Date.now() >= deadline) throw new Error('Source restore timed out');
};

// Bound the whole restore episode, including a transport promise that never settles.
const awaitRestore = async <T>(
  proxy: Proxy,
  work: Promise<T>,
  current: () => boolean,
  deadline: number
): Promise<T> => {
  const { signal } = getSnapshotScope(proxy).controller;
  let timer: ReturnType<typeof setTimeout>;
  let cancel: () => void = () => {};
  const stopped = new Promise<never>((_resolve, reject) => {
    cancel = () => reject(new DOMException('Restore owner retired', 'AbortError'));
    signal.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(
      () => reject(new Error('Source restore timed out')),
      Math.max(0, deadline - Date.now())
    );
    if (signal.aborted) cancel();
  });
  try {
    const result = await Promise.race([work, stopped]);
    requireRestoreCurrent(current, deadline);
    return result;
  } finally {
    clearTimeout(timer!);
    signal.removeEventListener('abort', cancel);
  }
};

/** Retry renews one source episode before authentication, including reset-time failures. */
const installSnapshotRetry = (
  proxy: Proxy,
  scope: SnapshotScope,
  tokenGenerator: () => Promise<string>
): (() => Promise<void>) => {
  const retry = async (): Promise<void> => {
    let next = getSnapshotScope(proxy);
    if (next.lifetime !== scope.lifetime || !isCurrentSnapshotScope(next))
      throw new DOMException('Restore owner retired', 'AbortError');
    // Retire prior callbacks and claims before admitting a new restoration episode.
    retireSnapshotScope(next);
    next = getSnapshotScope(proxy);
    clearTimeout(next.lifetime.timer);
    delete next.lifetime.timer;
    next.lifetime.deadline = Date.now() + 120_000;
    for (const device of next.lifetime.devices.values()) retainSourceView(proxy, device);
    const fresh = await awaitRestore(
      proxy,
      tokenGenerator(),
      () => isCurrentSnapshotScope(next),
      next.lifetime.deadline
    );
    await runner(function* () {
      yield* restoreAgent(proxy, fresh, tokenGenerator);
    });
  };
  scope.lifetime.retry = retry;
  return retry;
};

/**
 * @internal
 * Re-apply the pending-camera records of a saved snapshot that the bridge no
 * longer holds (it restarted), as one optimistic studio-only write: no LiveKit
 * token, no control.device entry, so nothing dials. Their tiles show from the
 * snapshot meanwhile, so an unreachable bridge does not hide them.
 */
const replayPendingAuthRecords = (proxy: Proxy, snapshot: Snapshot): void => {
  // Camera agents never host pending records and are not restored from snapshots.
  if (proxy.embedded) {
    return;
  }

  syncPersistedTransitoryDevices(
    proxy.id,
    pendingAuthPlaceholders(proxy.id, proxy.roomId, snapshot.control)
  );

  const held = proxy.state.control?.studio?.device ?? {};
  const missing = Object.entries(pendingAuthReplayRecords(snapshot.control)).filter(
    ([id]) => !held[id] && !isDeviceDeleted(id as Device['id'])
  );

  if (missing.length === 0) {
    return;
  }

  proxy
    .mutate({ control: { studio: { device: Object.fromEntries(missing) } } }, {}, true)
    .catch((cause: unknown) => {
      logger.warning(new StudioError('Unable to restore pending cameras.', { cause }));
    });
};

/**
 * Restore the agent's saved state.
 *
 * @internal
 */
export function* restoreAgent(
  proxy: Proxy,
  token: string,
  tokenGenerator: () => Promise<string>,
  observation?: ClaimBootstrapObservationRun
): Generator<unknown, void> {
  const scope = getSnapshotScope(proxy);
  const { status } = proxy.state;
  installSnapshotRetry(proxy, scope, tokenGenerator);

  // Capture ownership before storage; a reset must not restore the old reply.
  recordClaimBootstrapPhase(proxy, observation, 'snapshot-load', 'entered');
  const snapshot = yield* unwrap(loadSnapshot(proxy, token));
  recordClaimBootstrapPhase(proxy, observation, 'snapshot-load', 'returned');
  if (!isCurrentSnapshotScope(scope)) return;

  let restoredDeviceCount = 0;

  if (snapshot) {
    // First, so a device restore's save can never write a snapshot without them.
    replayPendingAuthRecords(proxy, snapshot);

    const orderedDeviceIds = Object.entries(snapshot.control?.studio?.device ?? {})
      .sort(
        ([_aID, a], [_bID, b]) =>
          (a?.rank ?? Number.POSITIVE_INFINITY) - (b?.rank ?? Number.POSITIVE_INFINITY)
      )
      .map(([id]) => id)
      .filter((id) => !!id);

    const savedDevices = orderedDeviceIds.map((key) => ({
      key,
      device: pbToJsDevice(proxy.id, key, snapshot, proxy.embedded) as unknown as Record<
        string,
        any
      >
    }));
    // Publish every saved row before any credential await, so one stalled source cannot hide its peers.
    for (const { device } of savedDevices) {
      if (
        device &&
        !proxy.embedded &&
        !status?.device[device.id] &&
        !isTransient(device.id) &&
        !isDeviceDeleted(device.id) &&
        !scope.seenDevices.has(device.id) &&
        !scope.lifetime.intents.has(device.id)
      ) {
        retainSourceView(proxy, device as unknown as Device);
      }
    }

    // Crack open the snapshot and bootstrap the agent.
    for (const { key, device: jsDevice } of savedDevices) {
      if (
        !jsDevice ||
        status?.device[jsDevice.id] ||
        isTransient(jsDevice.id) ||
        isDeviceDeleted(jsDevice.id)
      ) {
        continue;
      }

      if (proxy.embedded) {
        continue;
      }

      if (
        scope.pendingDevices.has(jsDevice.id) ||
        scope.seenDevices.has(jsDevice.id) ||
        scope.lifetime.deleted.has(jsDevice.id) ||
        scope.lifetime.intents.has(jsDevice.id)
      ) {
        // Record why restoration was skipped so deduplication mistakes remain diagnosable.
        void track({
          category: EVENT_CATEGORIES.DEVICE,
          name: EVENT_NAMES.DEVICE_RESTORE_SKIPPED,
          properties: {
            agent_id: proxy.id,
            device_id: jsDevice.id,
            // Pending and seen both mean claimed in this restore epoch.
            skip_reason:
              scope.pendingDevices.has(jsDevice.id) || scope.seenDevices.has(jsDevice.id)
                ? 'seen_in_restore_epoch'
                : scope.lifetime.deleted.has(jsDevice.id)
                  ? 'deleted_by_authority'
                  : 'source_change_in_progress'
          }
        } as AnalyticsEvent);
        continue;
      }

      if (scope.lifetime.deadline !== undefined && Date.now() >= scope.lifetime.deadline) continue;
      scope.lifetime.devices.set(jsDevice.id, jsDevice as unknown as Device);
      retainSourceView(proxy, jsDevice as unknown as Device);
      scope.seenDevices.add(jsDevice.id);
      const version = {};
      scope.versions.set(jsDevice.id, version);
      const current = (): boolean =>
        isCurrentSnapshotScope(scope) &&
        scope.versions.get(jsDevice.id) === version &&
        !scope.lifetime.deleted.has(jsDevice.id) &&
        !scope.lifetime.intents.has(jsDevice.id);

      scope.pendingDevices.add(jsDevice.id);
      let credentials: Awaited<ReturnType<typeof createToken>>;
      try {
        credentials = yield* unwrap(
          awaitRestore(
            proxy,
            createToken(key, proxy.roomId, token, jsDevice.name),
            current,
            scope.lifetime.deadline!
          )
        );
      } catch (cause) {
        if (!current()) throw new DOMException('Restore source retired', 'AbortError');
        // Only the generation still owning this pending claim may release it.
        if (scope.pendingDevices.delete(jsDevice.id)) scope.seenDevices.delete(jsDevice.id);
        throw cause;
      }
      if (!current()) return;
      const { token: deviceToken, endpoint } = credentials;
      const analyticsPriming = primeDeviceAnalyticsRegistry(
        jsDevice.modelId as Model['id'] | undefined,
        token
      );

      // Optional analytics metadata must not delay the visible snapshot restore.
      // Add device to the agent.
      // ENG-3885: catch permanent restore failures so they surface as a
      // user-visible notification instead of an unhandled promise rejection.
      // The retry closure refreshes both auth layers; Retry can run long
      // after the boot-time auth token and LiveKit device token have expired.
      restoreDevice(
        proxy,
        jsDevice as unknown as Device,
        deviceToken,
        endpoint,
        0,
        current,
        scope.lifetime.deadline!
      )
        .then(() => {
          // ENG-3885: automatic recovery must retire stale persistent failure UI.
          if (!current()) return;
          // A restored live device must accept settings mutations again.
          clearDeviceDeleted(jsDevice.id as Device['id']);
          clearRestoreFailure(toRestoreFailureMapKey('device', jsDevice.id as string, proxy.id));
        })
        .catch((e) => {
          if (!current()) return;
          scope.seenDevices.delete(jsDevice.id);
          handleRestoreFailure(
            'device',
            jsDevice.id as string,
            e,
            async () => {
              // A retry belongs to the live proxy/account, not to the retired reset callback.
              const next = getSnapshotScope(proxy);
              if (
                next.lifetime !== scope.lifetime ||
                next.lifetime.deleted.has(jsDevice.id) ||
                next.lifetime.intents.has(jsDevice.id)
              ) {
                throw new DOMException('Restore source retired', 'AbortError');
              }
              if (next.pendingDevices.has(jsDevice.id))
                throw new Error('Source restore already in progress');
              const retryVersion = {};
              next.versions.set(jsDevice.id, retryVersion);
              next.pendingDevices.add(jsDevice.id);
              const retryCurrent = (): boolean =>
                isCurrentSnapshotScope(next) &&
                next.versions.get(jsDevice.id) === retryVersion &&
                !next.lifetime.deleted.has(jsDevice.id) &&
                !next.lifetime.intents.has(jsDevice.id);
              try {
                const deadline = Date.now() + 120_000;
                clearTimeout(next.lifetime.timer);
                delete next.lifetime.timer;
                next.lifetime.deadline = deadline;
                retainSourceView(proxy, jsDevice as unknown as Device);
                const freshAuthToken = await awaitRestore(
                  proxy,
                  tokenGenerator(),
                  retryCurrent,
                  deadline
                );
                const credentials = await awaitRestore(
                  proxy,
                  createToken(key, proxy.roomId, freshAuthToken, jsDevice.name),
                  retryCurrent,
                  deadline
                );
                await restoreDevice(
                  proxy,
                  jsDevice as unknown as Device,
                  credentials.token,
                  credentials.endpoint,
                  0,
                  retryCurrent,
                  deadline
                );
              } finally {
                if (next.versions.get(jsDevice.id) === retryVersion)
                  next.pendingDevices.delete(jsDevice.id);
              }
            },
            proxy.id,
            jsDevice.name || (jsDevice.id as string)
          );
        })
        .finally(() => {
          if (scope.versions.get(jsDevice.id) === version) scope.pendingDevices.delete(jsDevice.id);
        });
      const emitDeviceRestoreAnalytics = (
        primedModel: T_Maybe<DeviceAnalyticsCatalogModelLike>
      ): void => {
        if (!current()) return;
        void track({
          category: EVENT_CATEGORIES.DEVICE,
          name: EVENT_NAMES.DEVICE_RESTORE,
          properties: {
            ...resolveDeviceAnalyticsContextFromDevice(jsDevice as Device, {
              proxy,
              studioId: proxy.roomId,
              // ENG-4456: carries the brand/model this restore's own priming fetch
              // resolved, so device_restore stops losing device_brand_id whenever it
              // beats some unrelated consumer to being the first request for this model.
              model: primedModel
            })
          }
        } as AnalyticsEvent);
      };
      void analyticsPriming.then(emitDeviceRestoreAnalytics, () =>
        emitDeviceRestoreAnalytics(undefined)
      );

      restoredDeviceCount++;
    }

    // Restore recording directory
    if (snapshot.control?.studio?.recordingDirectory) {
      // ENG-3885: surface permanent failures (see restoreDevice catch above).
      const recordingPath = snapshot.control.studio.recordingDirectory;
      restoreRecordingDirectory(proxy, recordingPath, 0, () => isCurrentSnapshotScope(scope))
        .then(() => {
          // ENG-3885: automatic recovery must retire stale persistent failure UI.
          if (!isCurrentSnapshotScope(scope)) return;
          clearRestoreFailure(
            toRestoreFailureMapKey('recordingDirectory', recordingPath, proxy.id)
          );
        })
        .catch(
          (e) =>
            isCurrentSnapshotScope(scope) &&
            handleRestoreFailure(
              'recordingDirectory',
              recordingPath,
              e,
              async () => {
                const next = getSnapshotScope(proxy);
                if (next.lifetime !== scope.lifetime)
                  throw new DOMException('Restore owner retired', 'AbortError');
                await restoreRecordingDirectory(proxy, recordingPath, 0, () =>
                  isCurrentSnapshotScope(next)
                );
              },
              proxy.id,
              'Recording directory'
            )
        );
    }

    // Restore RTMP destinations
    if (snapshot.control?.studio?.rtmpEgress?.destination) {
      // ENG-3885: surface permanent failures (see restoreDevice catch above).
      const rtmpDestinations = snapshot.control.studio.rtmpEgress.destination;
      restoreRTMPDestinations(proxy, rtmpDestinations, 0, () => isCurrentSnapshotScope(scope))
        .then(() => {
          // ENG-3885: automatic recovery must retire stale persistent failure UI.
          if (!isCurrentSnapshotScope(scope)) return;
          clearRestoreFailure(toRestoreFailureMapKey('rtmpDestinations', 'snapshot', proxy.id));
        })
        .catch(
          (e) =>
            isCurrentSnapshotScope(scope) &&
            handleRestoreFailure(
              'rtmpDestinations',
              'snapshot',
              e,
              async () => {
                const next = getSnapshotScope(proxy);
                if (next.lifetime !== scope.lifetime)
                  throw new DOMException('Restore owner retired', 'AbortError');
                await restoreRTMPDestinations(proxy, rtmpDestinations, 0, () =>
                  isCurrentSnapshotScope(next)
                );
              },
              proxy.id,
              'Streaming destinations'
            )
        );
    }

    // Restore IP controllers
    restoreIPControllers();
  }

  // The tray observes the same cameras but disables analytics delivery. Studio
  // owns fresh automatic adds, so the tray cannot consume first-shadow state
  // or insert a camera whose outcome Studio would mistake for a reconnect.
  // The `iris_role=tray` URL role is read at module load, so it holds from the
  // tray's first line of code. The localStorage flag alone is not enough: the
  // main process writes it only after the tray finishes loading, and each
  // launch serves the frontend on a new localhost origin, so on a relaunch it
  // starts empty and a tray that restores an already-linked camera before the
  // write sent its own device_shadowed (HIL run 37958378161, Windows, two
  // shadows for one camera).
  if (proxy.embedded && restoredDeviceCount === 0 && !isTrayRenderer()) {
    // start() also settles on its backstop or a replaced init; neither proves a live shadow.
    if (!proxy.isStarted) {
      let off = () => {};
      try {
        yield* unwrap(
          awaitRestore(
            proxy,
            new Promise<void>((resolve) => {
              off = proxy.on('state.started', resolve);
            }),
            () => isCurrentSnapshotScope(scope),
            scope.lifetime.deadline ?? Date.now() + 120_000
          )
        );
      } finally {
        // Per settle-every-terminal-branch (ai.knowledge/global): retire the waiter on every exit.
        off();
      }
    }
    const embeddedModelId = toHexFromId<Opaque<string, '__deviceModelId'>>(proxy.modelId)!;
    recordClaimBootstrapPhase(proxy, observation, 'template-load', 'entered');
    const {
      audio: templateAudio,
      control: templateControl,
      video: templateVideo
    } = yield* unwrap(getTemplate(embeddedModelId, token));
    recordClaimBootstrapPhase(proxy, observation, 'template-load', 'returned');
    // Same reason restore hands the primed model through: the shared registry
    // store can still miss this model at emit time even after the fetch ran.
    if (!isCurrentSnapshotScope(scope)) return;
    recordClaimBootstrapPhase(proxy, observation, 'analytics-model-load', 'entered');
    const primedShadowModel = yield* unwrap(primeDeviceAnalyticsRegistry(embeddedModelId, token));
    recordClaimBootstrapPhase(proxy, observation, 'analytics-model-load', 'returned');
    if (!isCurrentSnapshotScope(scope)) return;

    let audio: Minimist['audio'] | undefined;
    let control: Minimist['control'] | undefined;
    let video: Minimist['video'] | undefined;

    if (templateAudio !== undefined) {
      audio = {
        ...templateAudio
      };
    }

    if (templateControl !== undefined) {
      control = defaults({}, templateControl, {
        host: '0.0.0.0'
      });
    }

    if (templateVideo !== undefined) {
      video = defaults({}, templateVideo, {
        host: '0.0.0.0'
      });
    }

    const id = toEmbeddedShadowDeviceId(proxy.id);

    const candidate: Minimist = {
      id,
      agentId: proxy.id,
      modelId: toHexFromId(proxy.modelId),
      name: proxy.name,
      control,
      video,
      audio
    };

    const deviceAnalyticsContext = resolveDeviceAnalyticsContextFromDevice(
      {
        id,
        ...(candidate.modelId ? { modelId: candidate.modelId } : {})
      },
      {
        proxy,
        studioId: proxy.roomId,
        // device_shadowed and the shadow device_add share this bag. Without the
        // primed model they drop device_brand_id the same way restore used to.
        model: primedShadowModel
      }
    ) ?? { device_id: id };

    // Persisted once-flag read; it never throws and records nothing.
    recordClaimBootstrapPhase(proxy, observation, 'shadow-kind-read', 'entered');
    const shadowKindReading = yield* unwrap(readShadowKind(id, proxy.roomId));
    recordClaimBootstrapPhase(proxy, observation, 'shadow-kind-read', 'returned');
    // Record a previous launch before any overlapping bootstrap in this one
    // can read the in-memory flag and miss the relaunch.
    rememberPriorProcessShadow(shadowKindReading);
    // Claim and commit without yielding: cancelled reads consume no first, and
    // another bootstrap cannot turn this same reset interval into a return.
    // The current interval also permits an in-flight bootstrap to finish when
    // a newer reset was coalesced by the factory's existing reset throttle.
    if (!shadowReportState.has(proxy)) {
      shadowReportState.set(proxy, 'reported');
      const shadowKind = commitShadowKind(shadowKindReading);

      void track({
        category: EVENT_CATEGORIES.DEVICE,
        name: EVENT_NAMES.DEVICE_SHADOWED,
        properties: {
          ...deviceAnalyticsContext,
          agent_id: proxy.id,
          // Per ENG-3959 — this bootstrap path has one generated shadow camera id.
          target_device_id: id,
          target_bridge_id: proxy.id,
          source_device_id: id,
          // First vs reconnect: the cameras-shadowed funnel counts shadow_kind=first
          // only. First-ness is per Studio, so the same camera shadowed into a
          // second Studio on this install still counts as that Studio's first.
          shadow_kind: shadowKind
        }
      });
      recordClaimBootstrapPhase(proxy, observation, 'shadow-send-called', 'returned');
      // Only a sent shadow stops this Studio's claim-without-shadow clock.
      disarmDeviceShadowTimeout(proxy.id, proxy.roomId);
    }

    const studioDeviceState = snapshot?.control?.studio?.device?.[id] as T_Maybe<StudioDevice>;

    yield* unwrap(
      (async (): Promise<void> => {
        let counter = 0;
        let consecutiveSuccesses = 0;
        // The awaited attempt and the 2.5s retries are one add episode, and the
        // episode reports device_add once its own mutation inserts the camera,
        // or device_add_failed once the retries run out without it.
        // One exception: when the grace ends with this episode's own request
        // still unanswered, the failure is sent as add_unresolved, and if that
        // request then inserts the camera, a device_add with after_add_failed
        // follows. A later definitive rejection reports add_failed instead;
        // silence alone never confirms failure. A
        // thrown attempt is not that outcome, because a later retry can still
        // insert the camera.
        // A camera already in the Studio before this bootstrap is a reconnect,
        // not an add, so it reports neither. A previous launch's stored `re`
        // is that fact when local control.device is still empty. An
        // overlapping bootstrap in this same launch reads the in-memory flag
        // and must still report the add the first bootstrap did not finish
        // (broker.test.ts, "another bootstrap still adds the camera").
        // Per rule: reference-rules-in-comments (ai.knowledge/global).
        const isShadowInStudio = (): boolean => Boolean(proxy.state.control?.device?.[id]);
        const shadowExistedBefore =
          isShadowInStudio() || isPriorProcessShadow(shadowKindReading.key);
        let shadowAddInserted = false;
        let shadowAddSettled = false;
        let shadowAddThrew = false;
        // The grace ran out while this episode's own attempt had no answer, and
        // device_add_failed went out as add_unresolved. That attempt can still
        // insert the camera; its device_add then says it follows that failure.
        let shadowAddFailedUnresolved = false;
        // A slow attempt can outlive the retry loop; the episode has no outcome
        // until it lands, so settling waits for the loop to stop AND for every
        // attempt of this episode to finish.
        let shadowAttemptsInFlight = 0;
        let shadowLoopStopped = false;

        const emitShadowDeviceAdd = (): void => {
          // addDevice calls this on an acknowledged insert. A reconnect still
          // inserts when local state is empty, and that insert is not a new add.
          // An obsolete snapshot scope never reports for its replacement.
          if (
            shadowExistedBefore ||
            confirmedShadowAdds.has(shadowKindReading.key) ||
            shadowAddInserted ||
            (shadowAddSettled && !shadowAddFailedUnresolved) ||
            !isCurrentSnapshotScope(scope)
          ) {
            return;
          }
          const afterAddFailed = shadowAddFailedUnresolved;
          shadowAddInserted = true;
          shadowAddSettled = true;
          // Check and record without yielding: overlapping episodes may have
          // both observed empty state before either insertion was acknowledged.
          confirmedShadowAdds.add(shadowKindReading.key);
          // Only inserted devices count toward the embedded add funnel. A late
          // insert after an add_unresolved failure is still a real add; the
          // flag preserves that history. Readers exclude add_unresolved from
          // confirmed failures; camera ids cannot pair overlapping episodes.
          void track({
            category: EVENT_CATEGORIES.DEVICE,
            name: EVENT_NAMES.DEVICE_ADD,
            properties: {
              ...deviceAnalyticsContext,
              device_id: id,
              agent_id: proxy.id,
              is_embedded: true,
              linking_surface: resolveLinkingSurface(),
              ...(afterAddFailed ? { after_add_failed: true } : {})
            }
          });
        };

        const emitShadowAddFailure = (
          errorType: 'add_unresolved' | 'add_failed' | 'add_not_inserted'
        ): void => {
          // An obsolete snapshot scope never reports for its replacement.
          if (!isCurrentSnapshotScope(scope)) return;
          // Underlying error text can contain hosts or credentials; only these
          // fixed outcome labels belong in analytics.
          void track({
            category: EVENT_CATEGORIES.DEVICE,
            name: EVENT_NAMES.DEVICE_ADD_FAILED,
            properties: {
              device_id: id,
              agent_id: proxy.id,
              is_embedded: true,
              linking_surface: resolveLinkingSurface(),
              severity: EVENT_SEVERITY.S2_ERROR,
              error_type: errorType
            }
          });
        };

        // Per rule: settle-every-terminal-branch (ai.knowledge/global) — a
        // deadline may report unknown, but the owning request's eventual
        // rejection still needs its outcome.
        const settleShadowAdd = (): void => {
          if (shadowExistedBefore || confirmedShadowAdds.has(shadowKindReading.key)) {
            return;
          }
          if (shadowAddSettled) {
            if (
              shadowAddFailedUnresolved &&
              !shadowAddInserted &&
              shadowAttemptsInFlight === 0 &&
              shadowAddThrew
            ) {
              shadowAddFailedUnresolved = false;
              emitShadowAddFailure('add_failed');
            }
            return;
          }
          // Presence cannot prove that our unanswered operation inserted it.
          // Only addDevice's acknowledged mutation can claim this outcome.
          if (shadowAttemptsInFlight === 0 && isShadowInStudio()) {
            shadowAddSettled = true;
            return;
          }
          if (shadowAttemptsInFlight === 0) {
            // Another bootstrap or manual add holds the add lock; it reports
            // its own outcome.
            if (inFlightDeviceAdds.has(id)) {
              shadowAddSettled = true;
              return;
            }
            // A pending placeholder alone (the room provider's loading tile,
            // a restore) owns no outcome, and it can be refreshed after this
            // episode's last attempt stood back for it. Settling here would
            // leave the episode with no outcome at all, so look again until
            // the placeholder expires.
            if (isPendingDevice(id)) {
              setTimeout(settleShadowAdd, SHADOW_ADD_PENDING_RECHECK_MS);
              return;
            }
          }
          shadowAddSettled = true;
          // Attempts still in flight here means the grace timer fired with this
          // episode's own request unanswered: the lock is ours, and the add has
          // not happened yet but may still land.
          shadowAddFailedUnresolved = shadowAttemptsInFlight > 0;
          emitShadowAddFailure(
            shadowAddFailedUnresolved
              ? 'add_unresolved'
              : shadowAddThrew
                ? 'add_failed'
                : 'add_not_inserted'
          );
        };

        const settleShadowAddWhenIdle = (): void => {
          if (shadowLoopStopped && shadowAttemptsInFlight === 0) {
            settleShadowAdd();
          }
        };

        const attemptShadowAdd = async (): Promise<void> => {
          // An obsolete snapshot cannot retry or report a device add for its replacement.
          if (!isCurrentSnapshotScope(scope)) return;
          shadowAttemptsInFlight++;
          try {
            const added = await addDevice(
              proxy.roomId,
              proxy.id,
              candidate,
              token,
              true,
              studioDeviceState,
              emitShadowDeviceAdd
            );
            if (added) {
              emitShadowDeviceAdd();
            }
          } catch {
            shadowAddThrew = true;
            // An acknowledged insert already reported before persistence. A
            // rejected mutation cannot borrow another operation's visible id.
          } finally {
            shadowAttemptsInFlight--;
            settleShadowAddWhenIdle();
          }
        };

        const interval = setInterval(() => {
          if (!isCurrentSnapshotScope(scope)) {
            clearInterval(interval);
            return;
          }
          void attemptShadowAdd();
          counter++;

          if (proxy.state.status?.device?.[id]) {
            consecutiveSuccesses++;
          }

          if (counter >= 100 || consecutiveSuccesses >= 10) {
            clearInterval(interval);
            // The attempt just started is in flight, so it settles on finish.
            shadowLoopStopped = true;
            settleShadowAddWhenIdle();
            // An attempt that never returns (a request with no answer) must not
            // leave the episode without an outcome, so the wait is bounded.
            setTimeout(settleShadowAdd, SHADOW_ADD_SETTLE_GRACE_MS);
          }
        }, 2500);

        scope.controller.signal.addEventListener('abort', () => clearInterval(interval), {
          once: true
        });
        await attemptShadowAdd();

        if (isCurrentSnapshotScope(scope)) await proxy.emit('device.added', candidate.id);

        //await proxy.save(token, true);
      })()
    );

    return;
  }
}

/**
 * Bootstrap the agent from its persistent state.
 *
 * @internal
 */
function* bootstrap(
  proxy: Proxy,
  token: string,
  tokenGenerator: () => Promise<string>,
  options?: Options,
  observation?: ClaimBootstrapObservationRun
): Generator<unknown, void> {
  // Restore agent?
  const restore = options?.restore !== false;

  if (restore) {
    yield* restoreAgent(proxy, token, tokenGenerator, observation);
  } else {
    recordClaimBootstrapPhase(proxy, observation, 'restore-disabled', 'returned');
  }
  /*
  // Persist the agent state.
  try {
    yield proxy.save(token, !restore);
  } catch (error) {
    logger.error(<Error>error);
  }
  */
}

/**
 * Initialize the agent proxy.
 *
 * @internal
 */
function initialize(
  roomId: Room['id'],
  agentId: Agent['id'],
  args: {
    appId: AppId;
    brandId?: T_Maybe<Uint8Array>;
    embedded?: T_Maybe<boolean>;
    handlers: Handlers;
    modelId?: T_Maybe<Uint8Array>;
    tokenGenerator: () => Promise<string>;
  }
): ProxyImpl {
  const { handlers, ...rest } = args;
  const { handleError, handleTransaction } = handlers;

  // eslint-disable-next-line prefer-const
  let proxy: T_Maybe<ProxyImpl>;

  // Create state sync subordinate.
  const subordinate = new SyncSubordinate(
    new SyncOptions(State, {
      logger: new SyncLogger(3, [agentId]),
      mutationCallback(tx?: Transaction, error?: Error): void {
        if (error) {
          handleError(error);

          return;
        }

        if (tx) {
          // Readiness belongs to authority acceptance, not the room's lossy UI
          // forwarding path. A reset can retire init while its UI owner is absent.
          if (proxy && !getSourceLifetime(proxy).retired) proxy.acceptAuthoritySnapshot(tx);
          observeCameraRemoval(
            agentId,
            'state.transaction',
            (id) => ({
              controlPresent: !!tx.next?.message?.control?.device?.[id],
              studioPresent: !!tx.next?.message?.control?.studio?.device?.[id],
              statusPresent: !!tx.next?.message?.status?.device?.[id]
            }),
            {
              reset: tx.isReset,
              resync: tx.isResync,
              authority: tx.isAuthority,
              optimistic: tx.isOptimistic
            }
          );
          // W12 — state-sync observability. Per the W12 plan we log
          // EVENT TYPE + KEY FIELDS only, never the full payload —
          // state-sync mutations fire at high frequency and including
          // payload bytes here would flood the bundle. The call site
          // here is the central mutationCallback hit by every txn, so
          // one emitter wires every event in one place.
          //
          // Per `stay-in-scope` (ai.knowledge/global) — additive only.
          // The observability emitter is wrapped in try/catch internally
          // and forwards to the canonical W3 redacted logger, so it
          // cannot interfere with the existing reset / transaction
          // dispatch below.
          const txMsg = tx.next?.message as { control?: object; status?: object } | undefined;
          const topKeys: string[] = [];
          if (txMsg?.control) topKeys.push('control');
          if (txMsg?.status) topKeys.push('status');
          logStateSyncEvent(
            agentId,
            {
              isReset: tx.isReset,
              isInit: tx.isInit,
              isResync: tx.isResync,
              isAuthority: tx.isAuthority,
              isOptimistic: tx.isOptimistic
            },
            topKeys
          );

          if (tx.isReset === true) {
            // Reset ends deduplication so wiped sources can be restored again.
            if (proxy) {
              // Retire the outgoing generation synchronously: event delivery may yield to an old
              // storage/token completion.
              const outgoing = snapshotScopes.get(proxy);
              if (outgoing) retireSnapshotScope(outgoing);
              // Accepted reset inventory must reach the room even if no later subscription arrives.
              // Raw resets carry no inventory and retain uncertain rows.
              if (tx.accepted && !getSourceLifetime(proxy).retired) {
                acceptSourceTransaction(proxy, tx);
                const accepted = handleTransaction(tx, agentId);

                if (accepted) {
                  accepted.catch(handleError);
                }
              }
              // The queued listener must not retire newer traffic after this synchronous retirement.
              pendingResetHandoffs.set(proxy, (pendingResetHandoffs.get(proxy) ?? 0) + 1);
              // Clear only reporting on the actual connection's reset, including
              // resets whose restore runner is coalesced by the factory throttle.
              if (shadowReportState.get(proxy) !== 'stopped') {
                shadowReportState.delete(proxy);
              }
              void proxy.emit('state.reset');
            }

            return;
          }

          if (!proxy || getSourceLifetime(proxy).retired) return;
          acceptSourceTransaction(proxy, tx);
          const p = handleTransaction(tx, agentId);

          if (p) {
            p.catch(handleError);
          }
        }
      },
      natsConn: nats.agentConnection(agentId),
      optimisticMutations: true,
      schema: schema['agent.v1.State']!,
      stateId: agentId,
      typeRegistry: messageTypeRegistry
    })
  );

  const connection = nats.agentConnection(agentId);

  // Create the state sync agent.
  proxy = new ProxyImpl(roomId, agentId, {
    ...rest,
    conn: connection,
    subordinate,
    tokenGenerator: args.tokenGenerator
  });

  const autoTrackingManager = new AutoTrackingManager(
    proxy,
    handlers.handleAutoTrackingAnnotationMutation
  );

  proxy.setAutoTrackingManager(autoTrackingManager);

  let lastHardReset = 0;

  proxy.on('state.hardReset', () => {
    const now = Date.now();

    if (now - lastHardReset < 5e3) {
      return;
    }

    // Subordinate owns bounded recovery. A second pRetry layer here
    // stacked with synchronize()'s timer and amplified the storm.
    let resetTimeout: NodeJS.Timeout | undefined;

    void subordinate
      .synchronize('reset')
      .then(() => {
        proxy?.forcePing();

        clearTimeout(resetTimeout);

        resetTimeout = setTimeout(() => {
          resetPipelines();
        }, 5000);
      })
      .catch((error: unknown) => {
        logger.error(error as Error);
      });

    lastHardReset = now;
  });

  return proxy;
}

interface SourceLifetime {
  readonly accountId: string | undefined;
  readonly roomId: Room['id'];
  readonly deleted: Set<Device['id']>;
  readonly intents: Map<Device['id'], object>;
  readonly devices: Map<Device['id'], Device>;
  retired: boolean;
  deadline?: number;
  timer?: ReturnType<typeof setTimeout>;
  retry?: () => Promise<void>;
  // First/new-session snapshots cannot prove deletion by absence.
  lastAcceptedSessionId?: string;
}

const sourceLifetimes = new WeakMap<Proxy, SourceLifetime>();
// Callback-raised resets still waiting for their queued `state.reset` listener, per proxy.
const pendingResetHandoffs = new WeakMap<Proxy, number>();

const getSourceLifetime = (proxy: Proxy): SourceLifetime => {
  const previous = sourceLifetimes.get(proxy);
  const accountId = depot.getState().account?.id;
  if (previous) return previous;
  const lifetime: SourceLifetime = {
    accountId,
    roomId: proxy.roomId,
    deleted: new Set(),
    intents: new Map(),
    devices: new Map(),
    retired: false
  };
  sourceLifetimes.set(proxy, lifetime);
  return lifetime;
};

// Retained, undeleted rows must survive saves made from incomplete live control.
const retainedSourceRowIds = (proxy: Proxy): ReadonlySet<Device['id']> => {
  const lifetime = sourceLifetimes.get(proxy);
  const ids = new Set<Device['id']>();
  if (!lifetime) return ids;
  for (const id of lifetime.devices.keys()) if (!lifetime.deleted.has(id)) ids.add(id);
  return ids;
};

// Wire the proxy save path to the broker's retained-row bookkeeping without a broker<->proxy cycle.
setRetainedRowResolver(retainedSourceRowIds);

/** Retain the saved row separately from authority state while restoration is uncertain. */
export const retainSourceView = (proxy: Proxy, device: Device): void => {
  const scope = getSnapshotScope(proxy);
  if (!isCurrentSnapshotScope(scope) || scope.lifetime.deleted.has(device.id)) return;
  const lifetime = scope.lifetime;
  lifetime.devices.set(device.id, device);
  const deadline = (lifetime.deadline ??= Date.now() + 120_000);
  depot.getState().produce((draft) => {
    const room = draft.rooms[proxy.roomId];
    if (!room) return;
    room.devices[device.id] = {
      ...device,
      sourceRecovery: Date.now() >= deadline ? 'unavailable' : 'recovering'
    };
    if (device.video && !room.videoIds.includes(device.id)) room.videoIds.push(device.id);
    if (device.audio && !room.audioIds.includes(device.id)) room.audioIds.push(device.id);
  });
  if (lifetime.timer === undefined && Date.now() < deadline) {
    lifetime.timer = setTimeout(() => {
      delete lifetime.timer;
      if (!currentLifetime(scope)) return;
      // Only still-recovering rows should fail; accepted rows must not raise a stale notice.
      let recovering = false;
      depot.getState().produce((draft) => {
        const room = draft.rooms[proxy.roomId];
        for (const device of Object.values(room?.devices ?? {})) {
          if (device.agentId === proxy.id && device.sourceRecovery === 'recovering') {
            device.sourceRecovery = 'unavailable';
            recovering = true;
          }
        }
      });
      if (recovering && lifetime.retry)
        handleRestoreFailure(
          'snapshot',
          'snapshot',
          new Error('Source restoration timed out'),
          lifetime.retry,
          proxy.id,
          proxy.name || proxy.id
        );
    }, deadline - Date.now());
  }
};

/** Saved view for a Provider that mounts after storage has already completed. */
export const retainedSourceDevices = (proxy: Proxy): Device[] => {
  const scope = getSnapshotScope(proxy);
  return isCurrentSnapshotScope(scope)
    ? [...scope.lifetime.devices.values()]
        .filter((device) => !scope.lifetime.deleted.has(device.id))
        .map((device) => ({
          ...device,
          sourceRecovery:
            Date.now() >= (scope.lifetime.deadline ?? Infinity) ? 'unavailable' : 'recovering'
        }))
    : [];
};

/** Fence restoration before issuing a deliberate source change; failure releases only its own intent. */
export const beginSourceChange = (
  proxy: Proxy,
  id: Device['id'],
  removing: boolean
): { current: () => boolean; finish: (success: boolean) => void } => {
  const scope = getSnapshotScope(proxy);
  const wasDeleted = scope.lifetime.deleted.has(id);
  if (!removing) scope.lifetime.deleted.delete(id);
  const intent = {};
  scope.versions.set(id, intent);
  scope.seenDevices.delete(id);
  scope.pendingDevices.delete(id);
  unsetPendingDevice(id);
  scope.lifetime.intents.set(id, intent);
  const current = (): boolean => isCurrentSnapshotScope(scope) && scope.versions.get(id) === intent;
  return {
    current,
    finish(success) {
      if (scope.lifetime.intents.get(id) !== intent) return;
      scope.lifetime.intents.delete(id);
      if (!currentLifetime(scope)) return;
      if (!success && wasDeleted) scope.lifetime.deleted.add(id);
      if (success) {
        if (removing) scope.lifetime.deleted.add(id);
        else scope.lifetime.deleted.delete(id);
        delete scope.load;
        clearRestoreFailure(toRestoreFailureMapKey('device', id, proxy.id));
      }
    }
  };
};

const currentLifetime = (scope: SnapshotScope): boolean =>
  !scope.lifetime.retired &&
  sourceLifetimes.get(scope.proxy) === scope.lifetime &&
  scope.accountId === depot.getState().account?.id &&
  scope.lifetime.accountId === scope.accountId &&
  scope.lifetime.roomId === scope.proxy.roomId &&
  scope.roomId === scope.proxy.roomId;

/** Await the applied authority deletion, not the optimistic mutation or its transport acknowledgement. */
export const waitForSourceRemoval = async (proxy: Proxy, id: Device['id']): Promise<void> => {
  const scope = getSnapshotScope(proxy);
  if (scope.lifetime.deleted.has(id)) return;
  let off = () => {};
  try {
    await awaitRestore(
      proxy,
      new Promise<void>((resolve) => {
        off = proxy.on('state.tx', () => {
          if (scope.lifetime.deleted.has(id)) resolve();
        });
      }),
      () => isCurrentSnapshotScope(scope),
      Date.now() + 10_000
    );
  } finally {
    off();
  }
};

// Remove an unavailable source from storage and the retained view without a live mutation.
// Prune only this row; storage failure rejects and leaves the retained view intact.
export const removeRetainedSource = async (
  proxy: Proxy,
  id: Device['id'],
  token: string
): Promise<void> => {
  const scope = getSnapshotScope(proxy);
  const change = beginSourceChange(proxy, id, true);
  let ok = false;
  try {
    await (proxy as ProxyImpl).pruneStoredSource(id, token, change.current);
    if (!change.current()) throw new DOMException('Source owner retired', 'AbortError');
    scope.lifetime.devices.delete(id);
    depot.getState().produce((draft) => {
      const room = draft.rooms[proxy.roomId];
      if (!room) return;
      delete room.devices[id];
      room.videoIds = room.videoIds.filter((videoId) => videoId !== id);
      room.audioIds = room.audioIds.filter((audioId) => audioId !== id);
    });
    ok = true;
  } finally {
    change.finish(ok);
  }
};

/** Room publication uses the exact callback receipt, never an inSync flag or storage result. */
export const isCurrentSourceTransaction = (proxy: Proxy, tx: Transaction): boolean => {
  // Only the latest receipt in the current scope may publish into the room.
  const scope = snapshotScopes.get(proxy);
  return !!scope && scope.latestTransaction === tx && isCurrentSnapshotScope(scope);
};

/** Only validated current authority absence can remove a retained source. */
export const shouldRemoveSource = (proxy: Proxy, tx: Transaction, id: Device['id']): boolean => {
  const scope = snapshotScopes.get(proxy);
  if (!scope || !isCurrentSnapshotScope(scope)) return false;
  // Absence is classified when the result arrives, not after a delayed UI callback releases a claim.
  return scope.latestTransaction === tx && scope.lifetime.deleted.has(id);
};

/** Follow the canonical encoded URX path; empty ancestor unsets disappear from decoded messages. */
const unsetsSourceURX = (tx: Transaction, id: Device['id']): boolean => {
  if (tx.mutation && tx.state?.encode) {
    const path = tx.state.encode({ control: { device: { [id]: { urx: '' } } } });
    const covers = (operation: StateValue | undefined, target: StateValue): boolean => {
      if (!operation) return false;
      // Canonical Mutator removes scalar/empty ancestor values before descending.
      if (!Object.keys(operation.nested ?? {}).length && !operation.map?.length) return true;
      const field = Object.keys(target.nested ?? {})[0];
      if (field !== undefined)
        return covers(operation.nested?.[Number(field)], target.nested[Number(field)]!);
      const entry = target.map?.[0];
      if (entry)
        return covers(
          operation.map?.find((item) => item.key?.string === entry.key?.string)?.value,
          entry.value!
        );
      return true;
    };
    const field = Object.keys(path)[0];
    return field !== undefined && covers(tx.mutation.unset[Number(field)], path[Number(field)]!);
  }
  // Local receipt-only consumers can still classify an explicit decoded leaf/device unset.
  const deviceMap =
    tx.accepted?.kind === 'delta' ? tx.accepted.unset.message.control?.device : undefined;
  if (!deviceMap || !Object.hasOwn(deviceMap, id)) return false;
  const device = deviceMap[id];
  return (
    !device ||
    Object.keys(device).filter((key) => key !== '$type').length === 0 ||
    typeof device.urx === 'string'
  );
};

const acceptSourceTransaction = (proxy: Proxy, tx: Transaction): void => {
  const scope = getSnapshotScope(proxy);
  if (!isCurrentSnapshotScope(scope)) return;
  scope.latestTransaction = tx;
  // Raw/optimistic/error callbacks cannot certify authority absence.
  if (!tx.accepted) return;
  // Whole-map absence is valid only for a snapshot, never an unrelated accepted delta.
  const snapshot = tx.accepted?.kind === 'snapshot' ? tx.accepted.state : undefined;
  const delta = tx.accepted?.kind === 'delta' ? tx.accepted : undefined;
  const next = (snapshot ?? delta?.set)?.message.control?.device ?? {};
  const unset =
    delta?.unset.message.control?.device ??
    (tx.mutation && tx.state?.decode(tx.mutation.unset).message.control?.device);
  const wholeMap = !delta;
  // A new authority session cannot prove deletion by absence; retain missing rows for restore.
  // Compare session bytes by value because each transaction may carry a fresh Uint8Array.
  const acceptedSessionId = tx.state?.sessionId?.toString();
  const newAuthoritySession =
    !!snapshot && acceptedSessionId !== scope.lifetime.lastAcceptedSessionId;
  const explicitlyRemoved = (id: Device['id']): boolean =>
    delta
      ? unsetsSourceURX(tx, id) || next[id]?.urx === ''
      : !next[id]?.urx && unset?.[id] !== undefined;
  if (wholeMap) scope.confirmed.clear();
  for (const [id, device] of Object.entries(next)) {
    const deviceId = id as Device['id'];
    if (device?.urx && !explicitlyRemoved(deviceId)) {
      scope.confirmed.add(deviceId);
      if (!scope.lifetime.intents.has(deviceId)) scope.lifetime.deleted.delete(deviceId);
      if (!scope.pendingDevices.has(deviceId)) scope.seenDevices.add(deviceId);
      // A confirmed live device must accept settings mutations again.
      clearDeviceDeleted(deviceId);
    }
  }

  const known = new Set([
    ...scope.lifetime.devices.keys(),
    ...Object.values(depot.getState().rooms?.[proxy.roomId]?.devices ?? {})
      .filter((device) => device.agentId === proxy.id)
      .map((device) => device.id)
  ]);
  for (const id of known) {
    const removed = explicitlyRemoved(id);
    if ((next[id]?.urx && !removed) || (!wholeMap && !removed)) continue;
    scope.confirmed.delete(id);
    if (removed) {
      if (!scope.lifetime.intents.has(id)) scope.versions.delete(id);
      scope.pendingDevices.delete(id);
      scope.lifetime.intents.delete(id);
    }
    // Explicit removal still applies across an authority session change.
    const absentInNewSession = newAuthoritySession && !removed;
    if (
      (removed || (!scope.loading && !absentInNewSession)) &&
      !scope.pendingDevices.has(id) &&
      !scope.lifetime.intents.has(id)
    ) {
      scope.lifetime.deleted.add(id);
      // Drop retained identity so a later same-ID source cannot reappear as recovering.
      scope.lifetime.devices.delete(id);
      scope.seenDevices.delete(id);
      clearRestoreFailure(toRestoreFailureMapKey('device', id, proxy.id));
    }
  }
  // Record accepted session IDs so restart snapshots cannot certify absence.
  if (snapshot) {
    if (acceptedSessionId === undefined) delete scope.lifetime.lastAcceptedSessionId;
    else scope.lifetime.lastAcceptedSessionId = acceptedSessionId;
  }
  if ([...known].every((id) => scope.confirmed.has(id) || scope.lifetime.deleted.has(id))) {
    clearTimeout(scope.lifetime.timer);
    delete scope.lifetime.timer;
    delete scope.lifetime.deadline;
    // Current accepted inventory retires this incident, without touching other source failures.
    clearRestoreFailure(toRestoreFailureMapKey('snapshot', 'snapshot', proxy.id));
  }
};

interface SnapshotScope {
  /** Accepted presence belongs to this reset generation, not the reusable proxy lifetime. */
  readonly confirmed: Set<Device['id']>;
  readonly proxy: Proxy;
  readonly accountId: string | undefined;
  readonly roomId: Room['id'];
  readonly controller: AbortController;
  readonly pendingDevices: Set<Device['id']>;
  readonly seenDevices: Set<Device['id']>;
  readonly versions: Map<Device['id'], object>;
  readonly lifetime: SourceLifetime;
  dispose: () => void;
  latestTransaction?: Transaction;
  loading?: boolean;
  load?: Promise<Snapshot>;
  snapshotRevision?: number;
}

// Scope identity fences non-abortable storage/token replies as well as cached data.
const snapshotScopes = new Map<Proxy, SnapshotScope>();

const retireSnapshotScope = (scope: SnapshotScope): void => {
  if (snapshotScopes.get(scope.proxy) === scope) snapshotScopes.delete(scope.proxy);
  scope.controller.abort();
  forgetAgentSnapshotRead(scope.proxy.id, scope.proxy);
  for (const id of scope.pendingDevices) unsetPendingDevice(id);
  scope.seenDevices.clear();
  scope.pendingDevices.clear();
  scope.dispose();
};

/** @internal Test seam: retire the restore epoch exactly as a reset does. */
export const resetRestoreEpoch = (agentId: Agent['id']): void => {
  for (const [proxy, scope] of snapshotScopes) if (proxy.id === agentId) retireSnapshotScope(scope);
};

const isCurrentSnapshotScope = (scope: SnapshotScope): boolean =>
  snapshotScopes.get(scope.proxy) === scope &&
  !scope.controller.signal.aborted &&
  currentLifetime(scope) &&
  scope.accountId === depot.getState().account?.id &&
  scope.roomId === scope.proxy.roomId;

const getSnapshotScope = (proxy: Proxy): SnapshotScope => {
  const previous = snapshotScopes.get(proxy);
  if (previous && isCurrentSnapshotScope(previous)) return previous;
  if (previous) retireSnapshotScope(previous);

  const scope: SnapshotScope = {
    confirmed: new Set(),
    proxy,
    accountId: depot.getState().account?.id,
    roomId: proxy.roomId,
    controller: new AbortController(),
    pendingDevices: new Set(),
    seenDevices: new Set(),
    versions: new Map(),
    lifetime: getSourceLifetime(proxy),
    dispose: () => {}
  };
  snapshotScopes.set(proxy, scope);
  const retire = (): void => retireSnapshotScope(scope);
  const offStop = proxy.on('stopped', () => {
    clearAgentSnapshot(proxy.id, proxy);
    scope.lifetime.retired = true;
    clearTimeout(scope.lifetime.timer);
    retire();
  });
  const offAccount = depot.subscribe(() => {
    if (!isCurrentSnapshotScope(scope)) {
      clearAgentSnapshot(proxy.id, proxy);
      scope.lifetime.retired = true;
      clearTimeout(scope.lifetime.timer);
      for (const key of failedRestores.keys())
        if (key.startsWith(`${proxy.id}:`)) clearRestoreFailure(key);
      retire();
    }
  });
  scope.dispose = () => {
    offStop();
    offAccount();
  };
  return scope;
};

// Saved storage, including an empty snapshot, cannot authorize live source deletion.
export const loadSnapshot = (proxy: Proxy, token: string): Promise<Snapshot> => {
  const scope = getSnapshotScope(proxy);
  if (!isCurrentSnapshotScope(scope))
    return Promise.reject(new DOMException('Snapshot owner retired', 'AbortError'));
  if (
    scope.load &&
    (scope.loading || scope.snapshotRevision === agentSnapshotRevision(proxy.id, proxy))
  )
    return scope.load;

  scope.loading = true;
  const cacheLoadStartedAt = Date.now();
  const { signal } = scope.controller;
  let cancel!: () => void;
  let timer: ReturnType<typeof setTimeout>;
  // Every reader joins the same settling promise.
  const cancelled = new Promise<never>((_resolve, reject) => {
    cancel = () => reject(new DOMException('Snapshot owner retired', 'AbortError'));
    signal.addEventListener('abort', cancel, { once: true });
    timer = setTimeout(() => reject(new Error('Source snapshot timed out')), 120_000);
  });
  const read = (async (): Promise<Snapshot> => {
    const result = await loadAgentSnapshot(proxy.id, token, proxy, signal);
    if (!isCurrentSnapshotScope(scope)) {
      throw new DOMException('Snapshot owner retired', 'AbortError');
    }
    scope.snapshotRevision = agentSnapshotRevision(proxy.id, proxy);
    const topKeys = Object.keys(result).filter(
      (key) => (result as unknown as Record<string, unknown>)[key] !== undefined
    );
    // This measures load latency, not the age of the saved snapshot.
    logCacheLoad(proxy.id, Date.now() - cacheLoadStartedAt, topKeys);
    return result as Snapshot;
  })();
  scope.load = Promise.race([read, cancelled])
    .catch((cause: unknown) => {
      // Release both coalescing layers: a timed-out transport can still be unresolved.
      // Keep newer locally saved intent, but fence any late reply from the failed read.
      if (isCurrentSnapshotScope(scope)) forgetAgentSnapshotRead(proxy.id, proxy);
      delete scope.load;
      if (signal.aborted) throw cause;
      throw new StudioError("Unable to load agent's persistent state.", { cause });
    })
    .finally(() => {
      scope.loading = false;
      clearTimeout(timer!);
      signal.removeEventListener('abort', cancel);
    });
  return scope.load;
};

/** A consumer may publish only while its completed read still belongs to the active generation. */
export const isCurrentSnapshotLoad = (proxy: Proxy, load: Promise<Snapshot>): boolean => {
  const scope = snapshotScopes.get(proxy);
  return !!scope && scope.load === load && isCurrentSnapshotScope(scope);
};

/** Agent resource broker. */
export const broker = makeRoomBroker({
  /**
   * Create an agent connection.
   *
   * @param agentId - Agent identifier.
   * @param args - Factory arguments.
   * @param args.handlers - Synchronization handlers.
   * @param args.signal - Abort signal (optional)
   * @param args.options - Initialization options.
   * @returns Agent proxy.
   */
  create(
    agentId: Agent['id'],
    args: T_Optional<{
      brandId: T_Maybe<Uint8Array>;
      embedded: boolean;
      modelId: T_Maybe<Uint8Array>;
      name: string;
      options: Options;
      signal: AbortSignal;
    }> & {
      appId: AppId;
      handlers: Handlers;
      roomId: Room['id'];
      tokenGenerator: () => Promise<string>;
    }
  ): Proxy {
    const { signal, tokenGenerator, ...rest } = args;

    // Initialize the agent proxy.
    const proxy = initialize(args.roomId, agentId, { tokenGenerator, ...rest });
    recordClaimBootstrapPhase(proxy, undefined, 'proxy-created', 'returned');

    // Join idle detection's bounded START owner; the room signal retires this
    // caller even when token acquisition or state bootstrap has not completed.
    void startAgent(agentId, args.roomId, signal);

    const retire = (): void => {
      const scope = snapshotScopes.get(proxy);
      if (scope) retireSnapshotScope(scope);
    };
    const retireLifetime = (): void => {
      const lifetime = getSourceLifetime(proxy);
      lifetime.retired = true;
      clearTimeout(lifetime.timer);
      retire();
    };
    signal?.addEventListener('abort', retireLifetime, { once: true });
    proxy.on('stopped', () => signal?.removeEventListener('abort', retireLifetime));
    const initialScope = getSnapshotScope(proxy);
    if (signal?.aborted) retireLifetime();

    let lastReset = Number.NEGATIVE_INFINITY;

    proxy.on('state.reset', () => {
      recordClaimBootstrapPhase(proxy, undefined, 'reset-received', 'entered');
      // Callback-raised resets already retired their generation; consume the handoff.
      // Other raw resets must still retire current work.
      const handoffs = pendingResetHandoffs.get(proxy) ?? 0;
      if (handoffs > 0) {
        pendingResetHandoffs.set(proxy, handoffs - 1);
      } else {
        retire();
      }
      if (signal?.aborted) return;
      const scope = getSnapshotScope(proxy);
      installSnapshotRetry(proxy, scope, tokenGenerator);
      // Show loss before storage/auth resolves, without re-marking already confirmed rows.
      for (const device of Object.values(depot.getState().rooms?.[proxy.roomId]?.devices ?? {})) {
        if (device.agentId === proxy.id && !scope.confirmed.has(device.id))
          retainSourceView(proxy, device);
      }

      // Throttle starts, but retain one trailing restore owned by the latest reset.
      let timer: ReturnType<typeof setTimeout> | undefined;
      const cancel = (): void => clearTimeout(timer);
      const restore = (): void => {
        scope.controller.signal.removeEventListener('abort', cancel);
        if (!isCurrentSnapshotScope(scope)) return;
        lastReset = Date.now();

        void runner(
          function* generator(_signal: AbortSignal): Generator<unknown, void> {
            // ENG-4620 (main #1867): claim-bootstrap phase observation for the reset restore.
            const observation = openClaimBootstrapObservation(proxy, 'reset');
            let finished = false;
            try {
              recordClaimBootstrapPhase(proxy, observation, 'reset-token', 'entered');
              const token = yield* unwrap(
                awaitRestore(
                  proxy,
                  tokenGenerator(),
                  () => isCurrentSnapshotScope(scope),
                  scope.lifetime.deadline ?? Date.now() + 120_000
                )
              );
              recordClaimBootstrapPhase(proxy, observation, 'reset-token', 'returned');
              // A retired scope leaves `finished` false, so it records as cancelled.
              if (!isCurrentSnapshotScope(scope)) return;

              recordClaimBootstrapPhase(proxy, observation, 'bootstrap-enter', 'entered');
              yield* bootstrap(proxy, token, tokenGenerator, { restore: true }, observation);
              recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'returned');
              finished = true;
            } catch (error) {
              finished = true;
              recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'threw');
              throw error;
            } finally {
              if (!finished) {
                recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'cancelled');
              }
            }
          },
          { signal: scope.controller.signal }
        ).catch((error: Error) => {
          if (error.name === 'AbortError') return;
          if (isCurrentSnapshotScope(scope)) {
            handleRestoreFailure(
              'snapshot',
              'snapshot',
              error,
              installSnapshotRetry(proxy, scope, tokenGenerator),
              proxy.id,
              proxy.name || proxy.id
            );
            // The restore failure owns the Retry notice; keep diagnostics without a duplicate toast.
            logger.error(error);
          }
        });
      };
      const delay = Math.max(0, 5e3 - (Date.now() - lastReset));
      if (delay > 0) {
        // Deferred, not dropped: the trailing restore below still runs.
        recordClaimBootstrapPhase(proxy, undefined, 'reset-ignored', 'returned');
        scope.controller.signal.addEventListener('abort', cancel, { once: true });
        timer = setTimeout(restore, delay);
      } else restore();
    });

    void runner(
      function* generator(_signal_: AbortSignal): Generator<unknown, Proxy | undefined> {
        // ENG-4620 (main #1867): claim-bootstrap phase observation for the initial restore.
        const observation = openClaimBootstrapObservation(proxy, 'initial');
        let finished = false;
        try {
          // A retired scope leaves `finished` false, so it records as cancelled.
          if (!isCurrentSnapshotScope(initialScope)) return;
          recordClaimBootstrapPhase(proxy, observation, 'initial-token', 'entered');
          const token = yield* unwrap(
            awaitRestore(
              proxy,
              tokenGenerator(),
              () => isCurrentSnapshotScope(initialScope),
              Date.now() + 120_000
            )
          );
          recordClaimBootstrapPhase(proxy, observation, 'initial-token', 'returned');
          if (!isCurrentSnapshotScope(initialScope)) return;

          // Start the agent proxy
          recordClaimBootstrapPhase(proxy, observation, 'proxy-start', 'entered');
          yield proxy.start();
          recordClaimBootstrapPhase(proxy, observation, 'proxy-start', 'returned');
          if (!isCurrentSnapshotScope(initialScope)) return;

          // Bootstrap the agent.
          recordClaimBootstrapPhase(proxy, observation, 'bootstrap-enter', 'entered');
          yield* bootstrap(proxy, token, tokenGenerator, args.options, observation);
          recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'returned');
          finished = true;

          //console.log('$GPU ON BOOTSTRAP', proxy);
          // Ensure the auto tracking worker.
          //yield proxy.ensureAutoTrackingWorker();

          return proxy;
        } catch (error) {
          finished = true;
          recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'threw');
          throw error;
        } finally {
          if (!finished) {
            recordClaimBootstrapPhase(proxy, observation, 'bootstrap-exit', 'cancelled');
          }
        }
      },
      { signal: initialScope.controller.signal }
    ).catch((error: Error) => {
      if (error.name === 'AbortError') return;
      if (isCurrentSnapshotScope(initialScope)) {
        handleRestoreFailure(
          'snapshot',
          'snapshot',
          error,
          async () => {
            const next = getSnapshotScope(proxy);
            if (next.lifetime !== initialScope.lifetime || !isCurrentSnapshotScope(next))
              throw new DOMException('Restore owner retired', 'AbortError');
            const token = await awaitRestore(
              proxy,
              tokenGenerator(),
              () => isCurrentSnapshotScope(next),
              Date.now() + 120_000
            );
            await runner(function* () {
              yield* restoreAgent(proxy, token, tokenGenerator);
            });
          },
          proxy.id,
          proxy.name || proxy.id
        );
        // The restore failure owns the Retry notice; keep diagnostics without a duplicate toast.
        logger.error(error);
      }
    });

    return proxy;
  },

  /**
   * Destroy the agent connection.
   *
   * @param proxy - Agent proxy to be stopped.
   */
  destroy(proxy: Proxy): void {
    const impl = <ProxyImpl>proxy;
    retireAgentStart(impl.id, impl.roomId);
    const scope = snapshotScopes.get(proxy);
    if (scope) {
      scope.lifetime.retired = true;
      clearTimeout(scope.lifetime.timer);
      retireSnapshotScope(scope);
    }

    // A reset bootstrap's separate runner can outlive teardown's pending IO.
    // Its late first-kind read must not consume a marker or send a shadow.
    shadowReportState.set(proxy, 'stopped');
    recordClaimBootstrapPhase(proxy, undefined, 'proxy-destroyed', 'returned');

    // Stop the agent
    impl.stop();

    // Remove all event listeners.
    impl.clearListeners();

    // Perform comprehensive cleanup to prevent memory leaks
    // This clears LiveKit state, message buffers, and video references
    cleanupDeviceByHex(impl.id, {
      stopLiveKit: true,
      clearVideoRefs: false, // Don't clear all video refs, just this device
      force: true
    }).catch((error) => {
      logger.warning(`Error during device cleanup for ${impl.id}: ${error}`);
    });

    // ENG-3714: drop the agent's whole restore-dedupe epoch. The old cleanup
    // filtered on ids still present in state, so ids already wiped from state
    // marooned in the module-lifetime Set forever; per-agent keying lets us
    // delete unconditionally and keeps the map from growing unbounded.
    resetRestoreEpoch(impl.id);

    clearAgentSnapshot(impl.id, impl);

    // Its pending cameras belong to a connection that just ended.
    dropPersistedTransitoryDevices(impl.id);

    // ENG-3885: purge any failed-restore entries owned by this agent so
    // we don't leak retry closures (which capture the now-stopped proxy)
    // or persistent failure notifications. Keys are formatted
    // `${agentId}:${kind}:${id}`.
    const agentPrefix = `${impl.id}:`;
    let purged = false;
    const purgedNotificationIds = new Set<Notification['id']>();
    for (const key of failedRestores.keys()) {
      if (key.startsWith(agentPrefix)) {
        const failedRestore = failedRestores.get(key);
        failedRestores.delete(key);
        if (failedRestore) {
          failedRestore.stopAutoRetry();
          purgedNotificationIds.add(failedRestore.notificationId);
        }
        purged = true;
      }
    }
    for (const notificationId of purgedNotificationIds) {
      clearRestoreFailureNotificationIfUnused(notificationId);
    }
    if (purged) {
      notifyRestoreFailureSubscribers();
    }
  }
});

// @todo this should be merged with the add command
/**
 * @internal
 * Exported so focused tests can drive the real single-flight primitive
 * without going through restoreAgent. Do not consume from product callers.
 */
export const addDevice = async (
  roomId: Room['id'],
  agentId: Agent['id'],
  candidate: Minimist,
  token: string,
  embedded?: boolean,
  studioDeviceState?: T_Maybe<StudioDevice>,
  onInserted?: () => void
): Promise<boolean> => {
  const device = toDevice(candidate);

  // Claim before any await. The 10s pending TTL is not the in-flight lock.
  // See knowledge: addDevice-single-flight-pattern (ai.knowledge/studio.client).
  if (inFlightDeviceAdds.has(device.id) || isPendingDevice(device.id)) {
    return false;
  }

  inFlightDeviceAdds.add(device.id);
  setPendingDevice({
    id: device.id,
    name: studioDeviceState?.name ?? device.name,
    embedded: !!embedded,
    agentId: agentId
  });

  try {
    const proxy = broker.acquire(agentId);

    if (!proxy) {
      throw new Error('NQ-961');
    }

    const scope = getSnapshotScope(proxy);
    const current = (): boolean =>
      isCurrentSnapshotScope(scope) &&
      !scope.lifetime.deleted.has(device.id) &&
      !scope.lifetime.intents.has(device.id);
    if (proxy.state.control?.device[device.id]) {
      unsetPendingDevice(device.id);
      return false;
    }

    const { token: livekitToken, endpoint } = await createToken(
      device.id,
      roomId,
      token,
      device.name
    );

    requireRestoreCurrent(current, Number.POSITIVE_INFINITY);
    // Materialize protobuf device.
    const pbControl = jsToPbControl(
      device,
      {
        liveKit: toAuthentication(roomId, endpoint, livekitToken)
      },
      embedded
    );

    if (!pbControl) {
      throw new Error('CD-712');
    }

    if (studioDeviceState !== undefined) {
      if (pbControl.studio === undefined) {
        pbControl.studio = {};
      }

      if (pbControl.studio.device === undefined) {
        pbControl.studio.device = {};
      }

      pbControl.studio.device[device.id] = studioDeviceState;
    }

    await proxy.mutate(
      {
        control: pbControl
      },
      {},
      false
    );

    requireRestoreCurrent(current, Number.POSITIVE_INFINITY);

    // The acknowledged mutation belongs to this operation; persistence can
    // outlast the reporting grace or fail after the camera already exists.
    // Never infer ownership from a shared camera id observed by another add.
    onInserted?.();

    // If this is not a transient device, save the agent state.
    if (!isTransient(device.id)) {
      await proxy.save(token);
    }

    return true;
  } catch (cause) {
    unsetPendingDevice(device.id);

    throw new StudioError(`Unable to add device: ${device.id}`, { cause });
  } finally {
    // Per rule: settle-every-terminal-branch (ai.knowledge/global) — always release.
    inFlightDeviceAdds.delete(device.id);
  }
};
